import { useEffect, useState } from "react";
import { api, downloadBlob } from "@/lib/api";
import { formatFCFA, currentMonth, csvSafe } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileBarChart, FileDown, Home } from "lucide-react";
import { toast } from "sonner";

export default function Rapports() {
  const [mois, setMois] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [loyers, setLoyers] = useState({ rows: [], mois });
  const [maisons, setMaisons] = useState([]);
  const [maisonCsvId, setMaisonCsvId] = useState("");
  const [maisonCsvYear, setMaisonCsvYear] = useState(new Date().getFullYear());
  const [downloading, setDownloading] = useState(false);
  const [filterMaisonId, setFilterMaisonId] = useState("all");
  const [taxPct, setTaxPct] = useState(0);
  const [maisonMonthlyId, setMaisonMonthlyId] = useState("all");
  const [monthlyExportMois, setMonthlyExportMois] = useState(currentMonth());
  const [downloadingMonthly, setDownloadingMonthly] = useState(false);

  const load = async (m) => {
    const [s, l, hm, cfg] = await Promise.all([
      api.get("/rapports/summary"),
      api.get(`/rapports/loyers-attendus?mois=${m}`),
      api.get("/maisons"),
      api.get("/configuration"),
    ]);
    setSummary(s.data);
    setLoyers(l.data);
    setMaisons(hm.data || []);
    setTaxPct(Number(cfg.data?.tax_habitation_pct || 0));
    if (!maisonCsvId && hm.data?.length) setMaisonCsvId(hm.data[0].id);
  };

  useEffect(() => { load(mois); }, [mois]);

  const filteredRows = filterMaisonId === "all"
    ? loyers.rows
    : loyers.rows.filter((r) => r.maison_id === filterMaisonId);

  const totalAttendu = filteredRows.reduce((s, r) => s + r.attendu, 0);
  const totalPaye = filteredRows.reduce((s, r) => s + r.paye, 0);
  const totalSolde = filteredRows.reduce((s, r) => s + r.solde, 0);
  const totalTaxe = filteredRows.reduce((s, r) => {
    if (r.tax_habitation_active === false) return s;
    return s + Math.round((r.paye || 0) * taxPct / 100);
  }, 0);
  const totalNet = totalPaye - totalTaxe;
  const netOf = (paye, row) => {
    const p = Number(paye || 0);
    if (row && row.tax_habitation_active === false) return p;
    return p - Math.round(p * taxPct / 100);
  };

  const exportCSV = () => {
    const headers = taxPct > 0
      ? ["Locataire", "Maison", "Attendu", "Payé (brut)", `Taxe (${taxPct}%)`, "Net perçu", "Solde", "Statut"]
      : ["Locataire", "Maison", "Attendu", "Payé", "Solde", "Statut"];
    const lines = [headers.join(";")];
    filteredRows.forEach((r) => {
      const taxable = r.tax_habitation_active !== false;
      const taxRow = taxable ? Math.round((r.paye || 0) * taxPct / 100) : 0;
      const netRow = (r.paye || 0) - taxRow;
      if (taxPct > 0) {
        lines.push([csvSafe(r.locataire_nom), csvSafe(r.maison_nom), r.attendu, r.paye, taxRow, netRow, r.solde, csvSafe(r.statut)].join(";"));
      } else {
        lines.push([csvSafe(r.locataire_nom), csvSafe(r.maison_nom), r.attendu, r.paye, r.solde, csvSafe(r.statut)].join(";"));
      }
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const suffix = filterMaisonId === "all" ? "" : `_${maisons.find((m) => m.id === filterMaisonId)?.nom?.replace(/[^\w-]+/g, "_") || "maison"}`;
    a.href = url; a.download = `rapport_loyers_${mois}${suffix}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportMonthlyCSV = async () => {
    if (!monthlyExportMois) {
      toast.error("Sélectionnez un mois à exporter.");
      return;
    }
    setDownloadingMonthly(true);
    try {
      const res = await api.get(`/rapports/loyers-attendus?mois=${monthlyExportMois}`);
      const rows = (res.data?.rows || []).filter((r) =>
        maisonMonthlyId === "all" ? true : r.maison_id === maisonMonthlyId,
      );
      const headers = taxPct > 0
        ? ["Locataire", "Maison", "Attendu", "Payé (brut)", `Taxe (${taxPct}%)`, "Net perçu", "Solde", "Statut"]
        : ["Locataire", "Maison", "Attendu", "Payé", "Solde", "Statut"];
      const lines = [headers.join(";")];
      rows.forEach((r) => {
        const taxable = r.tax_habitation_active !== false;
        const taxRow = taxable ? Math.round((r.paye || 0) * taxPct / 100) : 0;
        const netRow = (r.paye || 0) - taxRow;
        if (taxPct > 0) {
          lines.push([csvSafe(r.locataire_nom), csvSafe(r.maison_nom), r.attendu, r.paye, taxRow, netRow, r.solde, csvSafe(r.statut)].join(";"));
        } else {
          lines.push([csvSafe(r.locataire_nom), csvSafe(r.maison_nom), r.attendu, r.paye, r.solde, csvSafe(r.statut)].join(";"));
        }
      });
      const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = maisonMonthlyId === "all"
        ? ""
        : `_${maisons.find((m) => m.id === maisonMonthlyId)?.nom?.replace(/[^\w-]+/g, "_") || "maison"}`;
      a.href = url;
      a.download = `rapport_loyers_${monthlyExportMois}${suffix}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export mensuel téléchargé");
    } catch (err) {
      toast.error("Téléchargement échoué");
    } finally {
      setDownloadingMonthly(false);
    }
  };

  const exportMaisonCSV = async () => {
    if (!maisonCsvId) {
      toast.error("Sélectionnez une maison à exporter.");
      return;
    }
    setDownloading(true);
    try {
      await downloadBlob(
        `/rapports/maison/${maisonCsvId}/csv?year=${maisonCsvYear}`,
        `rapport_maison_${maisonCsvYear}.csv`,
        "text/csv",
      );
      toast.success("Rapport CSV téléchargé");
    } catch (err) {
      toast.error("Téléchargement échoué");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Rapports"
        subtitle="Synthèse financière et état des loyers attendus, exportable au format CSV."
        testid="page-rapports"
        accent="blue"
      />

      <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white mb-6" data-testid="card-export-maison-monthly">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-teal-700 mb-1">Export mensuel</div>
            <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-teal-600" /> Rapport CSV du mois
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Exporte les loyers d'un mois donné (toutes maisons ou une seule) avec attendu, encaissé, taxe, net perçu et solde.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="min-w-[220px]">
              <Label className="text-xs">Maison</Label>
              <Select value={maisonMonthlyId} onValueChange={setMaisonMonthlyId}>
                <SelectTrigger data-testid="select-monthly-maison"><SelectValue placeholder="Sélectionnez…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les maisons</SelectItem>
                  {maisons.map((m) => <SelectItem key={m.id} value={m.id}>{m.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs">Mois (YYYY-MM)</Label>
              <Input
                type="month"
                value={monthlyExportMois}
                onChange={(e) => setMonthlyExportMois(e.target.value)}
                data-testid="input-monthly-mois"
              />
            </div>
            <Button
              onClick={exportMonthlyCSV}
              disabled={downloadingMonthly || !monthlyExportMois}
              className="bg-teal-600 hover:bg-teal-700 text-white shadow-sm shadow-teal-900/20"
              data-testid="btn-export-csv-monthly"
            >
              <FileDown className="w-4 h-4 mr-2" /> {downloadingMonthly ? "Téléchargement…" : "Exporter CSV"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white mb-6" data-testid="card-export-maison">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-orange-600 mb-1">Export annuel</div>
            <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
              <Home className="w-5 h-5 text-orange-500" /> Rapport CSV par maison
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Exporte 12 lignes (une par mois) avec loyer attendu, encaissé, solde, dépenses et résultat net.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="min-w-[220px]">
              <Label className="text-xs">Maison</Label>
              <Select value={maisonCsvId} onValueChange={setMaisonCsvId}>
                <SelectTrigger data-testid="select-csv-maison"><SelectValue placeholder="Sélectionnez…" /></SelectTrigger>
                <SelectContent>
                  {maisons.map((m) => <SelectItem key={m.id} value={m.id}>{m.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-28">
              <Label className="text-xs">Année</Label>
              <Input
                type="number"
                min={2000}
                max={2100}
                value={maisonCsvYear}
                onChange={(e) => setMaisonCsvYear(Number(e.target.value) || new Date().getFullYear())}
                data-testid="input-csv-year"
              />
            </div>
            <Button
              onClick={exportMaisonCSV}
              disabled={downloading || !maisonCsvId}
              className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm shadow-orange-900/20"
              data-testid="btn-export-csv-maison"
            >
              <FileDown className="w-4 h-4 mr-2" /> {downloading ? "Téléchargement…" : "Exporter CSV"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-xl border border-slate-200 shadow-sm bg-white">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">État des loyers</div>
            <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-emerald-600" /> Mois sélectionné
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <div>
              <Label className="text-xs">Filtrer par maison</Label>
              <Select value={filterMaisonId} onValueChange={setFilterMaisonId}>
                <SelectTrigger className="w-48" data-testid="select-filter-maison">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="filter-maison-all">Toutes les maisons</SelectItem>
                  {maisons.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`filter-maison-${m.id}`}>{m.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mois (YYYY-MM)</Label>
              <Input value={mois} onChange={(e) => setMois(e.target.value)} className="w-32" data-testid="input-rapport-mois" />
            </div>
          </div>
        </div>

        <div className={`grid grid-cols-1 ${taxPct > 0 ? "md:grid-cols-5" : "md:grid-cols-3"} gap-4 mb-5`}>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Attendu</div>
            <div className="font-heading text-xl font-bold text-slate-900 mt-1">{formatFCFA(totalAttendu)}</div>
          </div>
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Encaissé (brut)</div>
            <div className="font-heading text-xl font-bold text-emerald-800 mt-1" data-testid="rapport-total-brut">{formatFCFA(totalPaye)}</div>
          </div>
          {taxPct > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700">Taxe habitation ({taxPct}%)</div>
              <div className="font-heading text-xl font-bold text-amber-800 mt-1" data-testid="rapport-total-taxe">− {formatFCFA(totalTaxe)}</div>
            </div>
          )}
          {taxPct > 0 && (
            <div className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-3">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-teal-700">Net perçu</div>
              <div className="font-heading text-xl font-bold text-teal-800 mt-1" data-testid="rapport-total-net">{formatFCFA(totalNet)}</div>
            </div>
          )}
          <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-rose-700">Reste à recevoir</div>
            <div className="font-heading text-xl font-bold text-rose-800 mt-1">{formatFCFA(totalSolde)}</div>
          </div>
        </div>

        <div className="rounded-xl overflow-hidden border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Locataire</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maison</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Attendu</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Payé</TableHead>
                {taxPct > 0 && (
                  <TableHead className="text-xs uppercase tracking-wider text-teal-700 font-semibold text-right">Net perçu</TableHead>
                )}
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Solde</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.length === 0 && <TableRow><TableCell colSpan={taxPct > 0 ? 7 : 6} className="text-center text-slate-500 py-10" data-testid="rapport-empty">Aucune donnée pour ce filtre.</TableCell></TableRow>}
              {filteredRows.map((r) => (
                <TableRow key={r.locataire_id} className="hover:bg-slate-50/60" data-testid={`rapport-row-${r.locataire_id}`}>
                  <TableCell className="font-medium text-slate-900">{r.locataire_nom}</TableCell>
                  <TableCell className="text-slate-600">{r.maison_nom}</TableCell>
                  <TableCell className="text-right">{formatFCFA(r.attendu)}</TableCell>
                  <TableCell className="text-right">{formatFCFA(r.paye)}</TableCell>
                  {taxPct > 0 && (
                    <TableCell className="text-right font-semibold text-teal-800" data-testid={`rapport-net-${r.locataire_id}`}>{formatFCFA(netOf(r.paye, r))}</TableCell>
                  )}
                  <TableCell className={`text-right font-medium ${r.solde > 0 ? "text-rose-600" : "text-emerald-700"}`}>{formatFCFA(r.solde)}</TableCell>
                  <TableCell>
                    {r.statut === "payé" && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Payé</Badge>}
                    {r.statut === "partiel" && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200">Partiel</Badge>}
                    {r.statut === "impayé" && <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 border border-rose-200">Impayé</Badge>}
                    {r.statut === "temporaire" && <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 border border-orange-200">Temporaire</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
